import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { formatTime } from "@/lib/roninBox";
import { Timer, Pause, Play, SkipForward, Square, Bookmark, X, Trophy, ChevronLeft } from "lucide-react";

const CFG_KEY = "ronin_timer_config_v1";
const PRESET_KEY = "ronin_timer_presets_v1";
const DEFAULTS = { rounds: 3, roundSecs: 180, restSecs: 60, prepSecs: 10 };

const BUILT_IN = [
  { id: "b-pro", name: "Pro · 12×3", rounds: 12, roundSecs: 180, restSecs: 60, prepSecs: 10 },
  { id: "b-amateur", name: "Amateur · 3×3", rounds: 3, roundSecs: 180, restSecs: 60, prepSecs: 10 },
  { id: "b-spar", name: "Sparring · 5×3", rounds: 5, roundSecs: 180, restSecs: 60, prepSecs: 10 },
  { id: "b-hiit", name: "HIIT · 10×1", rounds: 10, roundSecs: 60, restSecs: 30, prepSecs: 5 },
];

const BELL_SRC = "/sounds/bell.wav";
const CLAP_SRC = "/sounds/clapper.wav";
const playSfx = (src) => { try { const a = new Audio(src); a.play().catch(() => {}); } catch { /* ignore */ } };
const playBell = () => playSfx(BELL_SRC);
const playDoubleBell = () => { playBell(); setTimeout(playBell, 620); };
const playClapper = () => playSfx(CLAP_SRC);

const loadLS = (k, fb) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fb; } catch { return fb; } };
const saveLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

export default function RoundTimer() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(DEFAULTS);
  const [presets, setPresets] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle|prep|round|rest|done
  const [roundIdx, setRoundIdx] = useState(1);
  const [remaining, setRemaining] = useState(0);
  const [paused, setPaused] = useState(false);

  const cfgRef = useRef(config);
  const phaseRef = useRef("idle");
  const roundIdxRef = useRef(1);
  const remainingRef = useRef(0);
  const deadlineRef = useRef(0);
  const pausedRef = useRef(false);
  const clappedRef = useRef(false);
  const intervalRef = useRef(null);

  useSEO({ title: "Round Timer — Ronin Box | Better Through Boxing", description: "A free boxing round timer with ring bells and the 10-second wooden clapper warning. Custom round times, rest breaks, and savable presets.", path: "/round-timer" });

  useEffect(() => { cfgRef.current = config; }, [config]);

  useEffect(() => {
    setConfig({ ...DEFAULTS, ...loadLS(CFG_KEY, {}) });
    setPresets(loadLS(PRESET_KEY, []));
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  useEffect(() => { if (phase === "idle") saveLS(CFG_KEY, config); }, [config, phase]);

  const setPhaseTo = (p, secs) => {
    phaseRef.current = p; setPhase(p);
    remainingRef.current = secs; setRemaining(secs);
    deadlineRef.current = Date.now() + secs * 1000;
  };
  const enterRound = (i) => {
    roundIdxRef.current = i; setRoundIdx(i);
    clappedRef.current = false;
    playBell();
    setPhaseTo("round", cfgRef.current.roundSecs);
  };
  const enterRest = () => setPhaseTo("rest", cfgRef.current.restSecs);
  const finish = () => { playDoubleBell(); phaseRef.current = "done"; setPhase("done"); remainingRef.current = 0; setRemaining(0); };
  const handlePhaseEnd = () => {
    const ph = phaseRef.current;
    if (ph === "prep") { enterRound(1); return; }
    if (ph === "round") {
      if (roundIdxRef.current >= cfgRef.current.rounds) finish();
      else { playBell(); enterRest(); }
      return;
    }
    if (ph === "rest") enterRound(roundIdxRef.current + 1);
  };
  const tick = () => {
    if (pausedRef.current) return;
    const rem = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
    if (rem !== remainingRef.current) { remainingRef.current = rem; setRemaining(rem); }
    if (phaseRef.current === "round" && rem <= 10 && rem > 0 && !clappedRef.current && cfgRef.current.roundSecs > 10) {
      clappedRef.current = true; playClapper();
    }
    if (rem <= 0) handlePhaseEnd();
  };
  const start = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    pausedRef.current = false; setPaused(false);
    playBell(); // also unlocks audio on the user gesture
    const c = cfgRef.current;
    if (c.prepSecs > 0) { setPhaseTo("prep", c.prepSecs); }
    else { clappedRef.current = false; roundIdxRef.current = 1; setRoundIdx(1); setPhaseTo("round", c.roundSecs); }
    intervalRef.current = setInterval(tick, 250);
  };
  const togglePause = () => {
    const next = !pausedRef.current; pausedRef.current = next; setPaused(next);
    if (!next) deadlineRef.current = Date.now() + remainingRef.current * 1000;
  };
  const skip = () => { remainingRef.current = 0; deadlineRef.current = Date.now(); handlePhaseEnd(); };
  const stop = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null; pausedRef.current = false; setPaused(false);
    phaseRef.current = "idle"; setPhase("idle");
  };

  const applyPreset = (p) => setConfig({ rounds: p.rounds, roundSecs: p.roundSecs, restSecs: p.restSecs, prepSecs: p.prepSecs ?? 0 });
  const isActivePreset = (p) => p.rounds === config.rounds && p.roundSecs === config.roundSecs && p.restSecs === config.restSecs && (p.prepSecs ?? 0) === config.prepSecs;
  const saveCurrentPreset = () => {
    const name = `${config.rounds}×${formatTime(config.roundSecs)} · ${formatTime(config.restSecs)} rest`;
    if (presets.some((p) => p.name === name)) return;
    const next = [...presets, { id: `c-${Date.now()}`, name, ...config }];
    setPresets(next); saveLS(PRESET_KEY, next);
  };
  const deletePreset = (id) => { const next = presets.filter((p) => p.id !== id); setPresets(next); saveLS(PRESET_KEY, next); };

  const totalSec = config.rounds * config.roundSecs + Math.max(0, config.rounds - 1) * config.restSecs + config.prepSecs;

  // ── Running full-screen ──────────────────────────────────────────────
  if (phase !== "idle") {
    const isRest = phase === "rest", isPrep = phase === "prep", isDone = phase === "done";
    const color = isDone ? "#FFFFFF" : isRest ? "#38BDF8" : isPrep ? "#A1A1AA" : "#FF331F";
    const label = isDone ? "Complete" : isRest ? "Rest" : isPrep ? "Get Ready" : `Round ${roundIdx} / ${config.rounds}`;
    const warn = phase === "round" && remaining <= 10 && remaining > 0;
    return (
      <div className="fixed inset-0 z-[120] bg-[#050505] text-white flex flex-col items-center justify-between py-10" data-testid="round-timer-running">
        <div className="text-xl sm:text-2xl uppercase tracking-[0.2em] font-black" style={{ color }} data-testid="timer-phase-label">{label}</div>
        <div className="flex flex-col items-center justify-center flex-1">
          {isDone ? (
            <>
              <Trophy className="h-16 w-16 text-[#FF331F]" />
              <div className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-6">Workout complete</div>
              <div className="text-zinc-400 mt-2">{config.rounds} rounds · {formatTime(totalSec)}</div>
            </>
          ) : (
            <div className="font-black tabular-nums leading-none" style={{ color: warn ? "#FF331F" : color, fontSize: "clamp(6rem, 22vw, 16rem)" }} data-testid="timer-countdown">
              {formatTime(remaining)}
            </div>
          )}
        </div>
        <div className="w-full max-w-md px-6">
          {isDone ? (
            <div className="flex flex-col gap-3">
              <button onClick={start} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-sm py-4 rounded-md transition-colors" data-testid="timer-restart">Start again</button>
              <button onClick={stop} className="w-full border border-[#27272A] text-zinc-300 hover:text-white font-bold uppercase tracking-widest text-sm py-4 rounded-md transition-colors" data-testid="timer-done">Done</button>
            </div>
          ) : (
            <div className="flex items-center justify-around border-t border-[#27272A] pt-6">
              <button onClick={togglePause} className="flex flex-col items-center gap-2 text-white hover:text-[#FF331F] transition-colors" data-testid="timer-pause">
                {paused ? <Play className="h-8 w-8" /> : <Pause className="h-8 w-8" />}
                <span className="text-[11px] uppercase tracking-widest font-bold">{paused ? "Resume" : "Pause"}</span>
              </button>
              <button onClick={skip} className="flex flex-col items-center gap-2 text-white hover:text-[#FF331F] transition-colors" data-testid="timer-skip">
                <SkipForward className="h-8 w-8" />
                <span className="text-[11px] uppercase tracking-widest font-bold">Skip</span>
              </button>
              <button onClick={stop} className="flex flex-col items-center gap-2 text-[#FF331F]" data-testid="timer-stop">
                <Square className="h-8 w-8" />
                <span className="text-[11px] uppercase tracking-widest font-bold">Stop</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Config screen ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="round-timer-page">
      <SiteHeader />
      <main className="max-w-2xl mx-auto px-6 md:px-10 py-12">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-zinc-500 hover:text-white text-xs uppercase tracking-widest font-bold mb-6" data-testid="timer-back">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-3 mb-2">
          <Timer className="h-8 w-8 text-[#FF331F]" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Ronin Box</p>
            <h1 className="font-display text-4xl uppercase tracking-tight">Round Timer</h1>
          </div>
        </div>
        <p className="text-zinc-400 mb-8">Bell-timed rounds with a 10-second wooden clapper warning. No cues — just your clock.</p>

        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-3">Presets</p>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="timer-presets">
          {[...BUILT_IN, ...presets].map((p) => {
            const active = isActivePreset(p);
            const custom = p.id?.startsWith("c-");
            return (
              <button key={p.id} onClick={() => applyPreset(p)} className={`inline-flex items-center rounded-full border px-4 py-2 text-xs font-bold transition-colors ${active ? "border-[#FF331F] bg-[#FF331F]/10 text-white" : "border-[#27272A] bg-[#0A0A0A] text-zinc-400 hover:text-white"}`} data-testid={`timer-preset-${p.id}`}>
                {p.name}
                {custom && (
                  <span onClick={(e) => { e.stopPropagation(); deletePreset(p.id); }} className="ml-2 text-zinc-500 hover:text-red-400" data-testid={`timer-preset-del-${p.id}`}><X className="h-3.5 w-3.5" /></span>
                )}
              </button>
            );
          })}
        </div>

        <div className="space-y-5">
          <Stepper label="Rounds" value={config.rounds} min={1} max={20} step={1} fmt={(v) => `${v}`} onChange={(v) => setConfig({ ...config, rounds: v })} testid="timer-rounds" />
          <Stepper label="Round time" value={config.roundSecs} min={30} max={600} step={15} fmt={formatTime} onChange={(v) => setConfig({ ...config, roundSecs: v })} testid="timer-roundsecs" />
          <Stepper label="Rest time" value={config.restSecs} min={0} max={300} step={15} fmt={formatTime} onChange={(v) => setConfig({ ...config, restSecs: v })} testid="timer-restsecs" />
          <Stepper label="Prep countdown" value={config.prepSecs} min={0} max={30} step={5} fmt={(v) => (v === 0 ? "Off" : `${v}s`)} onChange={(v) => setConfig({ ...config, prepSecs: v })} testid="timer-prepsecs" />
        </div>

        <button onClick={saveCurrentPreset} className="mt-5 inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-widest font-bold" data-testid="timer-save-preset">
          <Bookmark className="h-4 w-4" /> Save as preset
        </button>

        <div className="mt-6 border border-[#27272A] bg-[#0A0A0A] rounded-md p-5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Your session</p>
          <p className="font-display text-2xl uppercase tracking-tight mt-1">{config.rounds} × {formatTime(config.roundSecs)} · {formatTime(config.restSecs)} rest</p>
          <p className="text-xs text-zinc-500 mt-1">Total ~{formatTime(totalSec)}{config.prepSecs > 0 ? ` · ${config.prepSecs}s prep` : ""}</p>
        </div>

        <button onClick={start} className="mt-6 w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-sm py-4 rounded-md transition-colors" data-testid="timer-start">
          Start timer
        </button>
      </main>
      <SiteFooter />
    </div>
  );
}

function Stepper({ label, value, min, max, step, fmt, onChange, testid }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">{label}</p>
      <div className="flex border border-[#27272A] rounded-md overflow-hidden">
        <button onClick={() => onChange(Math.max(min, value - step))} disabled={value <= min} className="px-6 py-3 bg-[#0A0A0A] text-zinc-300 text-2xl font-bold disabled:opacity-30 hover:text-white" data-testid={`${testid}-dec`}>−</button>
        <div className="flex-1 flex items-center justify-center bg-[#050505]"><span className="text-3xl font-black tabular-nums">{fmt(value)}</span></div>
        <button onClick={() => onChange(Math.min(max, value + step))} disabled={value >= max} className="px-6 py-3 bg-[#0A0A0A] text-zinc-300 text-2xl font-bold disabled:opacity-30 hover:text-white" data-testid={`${testid}-inc`}>+</button>
      </div>
    </div>
  );
}
